import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import FileUpload from '../components/FileUpload';
import { useAlert } from '../context/AlertContext';

const ReportHazard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showWarning } = useAlert();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]); // Store files for upload

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    hazard_type: '',
    severity: '',
    incident_date: '',
    location: '',
    equipment_involved: '',
    immediate_action: '',
    root_cause: '',
    corrective_action: '',
    recommended_actions: '',
    responsible_person: '',
    target_date: '',
    is_lti: false,
    medical_leave_days: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoFilesChange = (files) => {
    setPhotoFiles(files);
  };

  const handlePhotoUploadError = (errors) => {
    errors.forEach(err => showWarning(err));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setAnalyzing(true);

    try {
      // Clean up form data before submission
      const submissionData = { ...formData };

      // If hazard_type is Lost Time Injury, ensure is_lti is true
      if (submissionData.hazard_type === 'Lost Time Injury') {
        submissionData.is_lti = true;
      }

      // Validate LTI fields when required
      const isLtiRequired = submissionData.is_lti || submissionData.hazard_type === 'Lost Time Injury';
      if (isLtiRequired && (!submissionData.medical_leave_days || submissionData.medical_leave_days === '')) {
        setError('Medical Leave Days is required when Lost Time Injury is selected');
        setLoading(false);
        setAnalyzing(false);
        return;
      }

      // Convert medical_leave_days to number or null
      if (submissionData.medical_leave_days === '' || !isLtiRequired) {
      // Convert medical_leave_days to float or null
      // if (submissionData.medical_leave_days === '' || !submissionData.is_lti) {
        submissionData.medical_leave_days = null;
      } else if (submissionData.medical_leave_days) {
        submissionData.medical_leave_days = parseFloat(submissionData.medical_leave_days);
      }

      // Create the hazard report (without photo - will upload to S3 separately)
      const response = await axios.post(`${API}/hazards`, submissionData);
      const hazardId = response.data.id;

      // Upload photo to S3 if a file was selected (from local files, not already uploaded)
      if (photoFiles.length > 0 && hazardId) {
        const photoToUpload = photoFiles[0];
        // Only upload if it's a local file (not already uploaded)
        if (photoToUpload.file && !photoToUpload.uploaded) {
          try {
            const photoFormData = new FormData();
            photoFormData.append('file', photoToUpload.file);

            await axios.post(`${API}/hazards/${hazardId}/upload-photo`, photoFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          } catch (photoErr) {
            // Log photo upload error but don't fail the entire submission
            // The hazard was created successfully, photo can be added later
            console.error('Photo upload error:', photoErr.response?.data);
          }
        }
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/hazards');
      }, 2000);
    } catch (err) {
      console.error('Incident submission error:', err.response?.data);

      // Handle validation errors from backend
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;

        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(e => `${e.loc?.join(' > ') || 'Field'}: ${e.msg}`).join(', ');
          setError(errorMessages);
        }
        // If detail is a string
        else if (typeof detail === 'string') {
          setError(detail);
        }
        // If detail is an object
        else {
          setError(JSON.stringify(detail));
        }
      } else {
        setError('Failed to submit incident report');
      }
    } finally {
      setLoading(false);
      setAnalyzing(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center justify-center h-96" data-testid="success-message">
          <div className="bg-green-100 rounded-full p-6 mb-4">
            <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Incident Reported Successfully!</h2>
          <p className="text-gray-600">AI analysis complete. Redirecting to incidents list...</p>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-3xl mx-auto" data-testid="report-hazard-page">
        <div className="bg-white rounded-xl shadow-md p-8">
          <h1 className="text-3xl font-bold text-[#1B5C94] mb-2">REPORT NEW INCIDENT</h1>
          <p className="text-gray-600 mb-2">Fill in the details below to report a workplace incident</p>
          
          <div className="bg-blue-50 border-l-4 border-[#1B5C94] p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-[#1B5C94] mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-[#1B5C94]">AI-Assisted Analysis</p>
                <p className="text-xs text-gray-700 mt-1">You control the Incident Type and Severity. Our AI can optionally assist with: Equipment Analysis, Root Cause, Corrective Actions, and Recommended Assignee (if you leave those fields blank).</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Incident Title */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
                Incident Title <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="hazard-title-input"
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="Brief title for the incident"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                data-testid="hazard-description-input"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="5"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="Detailed description of the incident. Be specific about what happened, when, and any potential risks."
                required
              />
              <p className="text-xs text-gray-500 mt-1">💡 Tip: Include details like time of occurrence, specific location, and potential impact for better AI analysis.</p>
            </div>

            {/* Incident Type and Severity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="hazard_type">
                  Incident Type <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="hazard-type-select"
                  id="hazard_type"
                  name="hazard_type"
                  value={formData.hazard_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  required
                >
                  <option value="">Select Incident Type</option>
                  <option value="Near-Miss">Near-Miss</option>
                  <option value="First Aid">First Aid</option>
                  <option value="Medical Treatment">Medical Treatment</option>
                  <option value="Restricted Work">Restricted Work</option>
                  <option value="Lost Time Injury">Lost Time Injury</option>
                  <option value="Property Damage">Property Damage</option>
                  <option value="Environmental">Environmental</option>
                  <option value="Unsafe Condition">Unsafe Condition</option>
                  <option value="Unsafe Act">Unsafe Act</option>
                  <option value="Equipment Failure">Equipment Failure</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="severity">
                  Severity Level <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="severity-select"
                  id="severity"
                  name="severity"
                  value={formData.severity}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  required
                >
                  <option value="">Select Severity</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </div>

            {/* Incident Date */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="incident_date">
                Date of Incident <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="incident-date-input"
                id="incident_date"
                name="incident_date"
                type="date"
                value={formData.incident_date}
                onChange={handleInputChange}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">When did this incident occur?</p>
            </div>

            {/* Location */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="location">
                Location
              </label>
              <input
                data-testid="hazard-location-input"
                id="location"
                name="location"
                type="text"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="Where did this occur? (e.g., Workshop Floor 2, Warehouse Section B)"
              />
            </div>

            {/* Equipment Involved */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="equipment_involved">
                Equipment Involved
              </label>
              <input
                data-testid="equipment-involved-input"
                id="equipment_involved"
                name="equipment_involved"
                type="text"
                value={formData.equipment_involved}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="Any equipment or machinery involved? (e.g., Forklift, Ladder, Conveyor Belt)"
              />
            </div>

            {/* Immediate Action */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="immediate_action">
                Immediate Action Taken
              </label>
              <textarea
                data-testid="immediate-action-input"
                id="immediate_action"
                name="immediate_action"
                value={formData.immediate_action}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="What immediate actions were taken to address this hazard?"
              />
            </div>

            {/* Lost Time Injury Flag */}
            <div className={`border-2 rounded-lg p-4 ${formData.hazard_type === 'Lost Time Injury' ? 'bg-orange-50 border-orange-300' : 'bg-yellow-50 border-yellow-200'}`}>
              <label className="flex items-center cursor-pointer">
                <input
                  data-testid="is-lti-checkbox"
                  id="is_lti"
                  name="is_lti"
                  type="checkbox"
                  checked={formData.is_lti || formData.hazard_type === 'Lost Time Injury'}
                  onChange={(e) => {
                    // Prevent unchecking if hazard_type is Lost Time Injury
                    if (formData.hazard_type === 'Lost Time Injury' && !e.target.checked) {
                      return;
                    }
                    setFormData(prev => ({ ...prev, is_lti: e.target.checked }));
                  }}
                  disabled={formData.hazard_type === 'Lost Time Injury'}
                  className="w-5 h-5 text-[#1B5C94] border-gray-300 rounded focus:ring-[#1B5C94] focus:ring-2 disabled:bg-orange-200"
                />
                <span className="ml-3">
                  <span className="block text-sm font-bold text-gray-800">
                    Lost Time Injury (LTI)
                    {formData.hazard_type === 'Lost Time Injury' && <span className="text-red-500 ml-1">*</span>}
                  </span>
                  <span className="block text-xs text-gray-600 mt-1">
                    {formData.hazard_type === 'Lost Time Injury'
                      ? 'Required because Incident Type is "Lost Time Injury"'
                      : 'Check this box if this incident resulted in time away from work'}
                  </span>
                </span>
              </label>

              {/* Medical Leave Days - Conditional */}
              {(formData.is_lti || formData.hazard_type === 'Lost Time Injury') && (
                <div className="mt-3 pt-3 border-t border-yellow-300">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="medical_leave_days">
                    Medical Leave Days <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="medical_leave_days"
                    name="medical_leave_days"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.medical_leave_days}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                    placeholder="Enter number of medical leave days"
                    required
                  />
                  <p className="text-xs text-gray-600 mt-1">Required for statutory reporting compliance</p>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Root Cause & Actions (Optional)</h3>
              <p className="text-sm text-gray-600 mb-4">💡 Leave blank to auto-generate with AI, or enter your own analysis</p>
              
              {/* Root Cause Analysis */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="root_cause">
                  Root Cause Analysis
                </label>
                <textarea
                  id="root_cause"
                  name="root_cause"
                  value={formData.root_cause}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  placeholder="Why did this incident occur? Identify underlying causes... (AI will generate if left blank)"
                />
              </div>

              {/* Corrective Action */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="corrective_action">
                  Corrective Action
                </label>
                <textarea
                  id="corrective_action"
                  name="corrective_action"
                  value={formData.corrective_action}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  placeholder="Specific steps to prevent recurrence... (AI will generate if left blank)"
                />
              </div>

              {/* Recommended Actions */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recommended_actions">
                  Recommended Future Actions
                </label>
                <textarea
                  id="recommended_actions"
                  name="recommended_actions"
                  value={formData.recommended_actions}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  placeholder="Enter preventive measures (separate with semicolons: Action 1; Action 2; Action 3)... (AI will generate if left blank)"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple actions with semicolons (;)</p>
              </div>
            </div>

            {/* Photo Upload */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Photo Upload (Optional)
              </label>
              <FileUpload
                files={photoFiles}
                onFilesChange={handlePhotoFilesChange}
                multiple={false}
                accept="image/*"
                maxSizeMB={10}
                label="Click to upload image"
                showSizeLimit={true}
                onError={handlePhotoUploadError}
                data-testid="photo-upload-input"
              />
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Follow-up Information (Optional)</h3>
              
              {/* Responsible Person */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="responsible_person">
                  Responsible Person
                </label>
                <input
                  data-testid="responsible-person-input"
                  id="responsible_person"
                  name="responsible_person"
                  type="text"
                  value={formData.responsible_person}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  placeholder="Person responsible for follow-up (if known)"
                />
              </div>

              {/* Target Date */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="target_date">
                  Target Resolution Date
                </label>
                <input
                  data-testid="target-date-input"
                  id="target_date"
                  name="target_date"
                  type="date"
                  value={formData.target_date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-alert">
                {error}
              </div>
            )}

            {analyzing && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#1B5C94] mr-3"></div>
                  <span className="text-[#1B5C94] font-semibold">AI is analyzing your report...</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              data-testid="submit-hazard-button"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting & Analyzing...
                </span>
              ) : (
                '🚀 SUBMIT INCIDENT REPORT'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
      </div>
  );
};

export default ReportHazard;